import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {useNavigate} from "react-router-dom";
import {LoggedInView} from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {Link, useParams} from "react-router-dom";
import {
    createService,
    resetCreateServiceStatus,
} from "../../store/reducers/ServicesSlice";
import {RequestStatus} from "../../utils/Enums";
import ElogCheckbox from "../common/form/ElogCheckbox";
import {getOrderComponents} from "../../store/reducers/OrderComponents";
import OrderComponent from "../../models/OrderComponent";
import {FormikProvider, useFormik, Field, Form} from "formik";
import {createExtension} from "../../store/reducers/ExtensionsSlice";

interface NewServiceScreenProps {
}

export interface NewServiceFormProps {
    name: string;
    englishName: string;
    order: number;
    clientEmail: boolean;
    vendorEmail: boolean;
    clientContactEmail: boolean;
    vendorContactEmail: boolean;
    noAutoInvoice: boolean;
    noValue: boolean;
    nameOnInvoice: boolean;
    railroadAndSea: boolean;
    invoiceOnAdd: boolean;
    mailsOnAdd: boolean;
    headsUpEmail: boolean;
    excludeFromClientConversion: boolean;
    orderComponents: {
        [id: number]: {
            mandatory: boolean;
            service?: number;
        };
    };
}

export const NewServiceScreen: React.FC<NewServiceScreenProps> = () => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const servicessState = useAppSelector(state => state.services);
    const orderComponentsState = useAppSelector((state) => state.orderComponents);

    const requestStatus: RequestStatus = servicessState.createServiceStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: 'Teenused', url: '/services'},
        {text: 'Lisa teenus', url: '/services/new'},
    ];

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            englishName: "",
            excludeFromClientConversion: false,
        },
        onSubmit: (values: any) => {
            const orderComponentsObject = Object.keys(values.orderComponents)
                .filter(id => {
                    const component = values.orderComponents[id as any];
                    return component && component.service;
                })
                .map(id => ({
                    id: parseInt(id),
                    mandatory: values.orderComponents[id as any].mandatory !== undefined ?
                        values.orderComponents[id as any].mandatory :
                        'true',
                }))
                .reduce<{ [key: number]: { mandatory: boolean } }>((acc, component) => {
                    acc[component.id] = { mandatory: component.mandatory };
                    return acc;
                }, {});

            const updatedValues: NewServiceFormProps = {
                ...values,
                orderComponents: orderComponentsObject,
            };

            dispatch(createService(updatedValues));

        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    const renderOrderComponent = (orderComponent: OrderComponent, indentLevel = 0): JSX.Element => (
        <React.Fragment key={orderComponent.id}>
            <tr>
                <td style={{ paddingLeft: `${indentLevel * 25}px`, paddingTop: '5px', paddingBottom: '5px' }}>
                    <Field
                        name={`orderComponents[${orderComponent.id}].service`}
                        component="input"
                        type="checkbox"
                    />
                </td>
                <td style={{ paddingLeft: `${indentLevel * 40}px`, paddingTop: '5px', paddingBottom: '5px' }}>
                    {orderComponent.name}
                </td>
                <td style={{ paddingLeft: `${indentLevel * 40}px`, paddingTop: '5px', paddingBottom: '5px' }}>
                    <Field
                        name={`orderComponents[${orderComponent.id}].mandatory`}
                        component="select"
                        defaultValue={'true'}
                    >
                        <option value={'true'}>Jah</option>
                        <option value={'false'}>Ei</option>
                    </Field>
                </td>
            </tr>
            {orderComponent.children &&
            orderComponent.children.map((child) =>
                renderOrderComponent(child, indentLevel + 1)
            )}
        </React.Fragment>
    );

    useEffect(() => {
        if (requestStatus === RequestStatus.Success) {
            dispatch(resetCreateServiceStatus());
            navigate(breadcrumbLinks[0].url);
        }

    }, [requestStatus]);

    useEffect(() => {
        dispatch(getOrderComponents());
    }, []);

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Failed && servicessState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {servicessState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2><span>Teenuse andmed</span></h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Nimi" name="name" mandatory={true}/>
                            <ElogInput label="Nimi inglise keeles" name="englishName"/>
                            {/*<ElogInput label="Kood raamatupidamises" name="account" mandatory={true}/>*/}
                            <ElogCheckbox label="Saada kliendi kiri" name="clientEmail"/>
                            <ElogCheckbox label="Saada hankija kiri" name="vendorEmail"/>
                            <ElogCheckbox label="Saada pealelaadija kontakti kiri" name="clientContactEmail"/>
                            <ElogCheckbox label="Saada mahalaadija kontakti kiri" name="vendorContactEmail"/>
                            <ElogCheckbox label="Arvet ei saadeta automaatselt välja" name="noAutoInvoice"/>
                            <ElogCheckbox label="Alarida ei nõua kauba väärtust" name="noValue"/>
                            <ElogCheckbox label="Arvel kuvatakse teenuse nimi" name="nameOnInvoice"/>
                            <ElogCheckbox label="Mere- ja raudteeveod +- 4 kuud tabelis" name="railroadAndSea"/>
                            <ElogCheckbox label="Toll" name="isCustoms"/>
                            <ElogCheckbox label="Veosekindlustus" name="isInsurance"/>
                            <ElogCheckbox label="Kirjad veo lisamisel" name="mailsOnAdd"/>
                            <ElogCheckbox label="Heads up kiri checkerile" name="headsUpEmail"/>
                            <ElogCheckbox label="Arve veo lisamisel" name="invoiceOnAdd"/>
                            <ElogCheckbox label="Arve laadimisel" name="invoiceOnLoading"/>
                            <ElogCheckbox label="Ei arvesta kliendi tellimuste protsendis" name="excludeFromClientConversion"/>
                            </tbody>
                        </table>
                    </div>

                    <div className="order-components-section">
                        <h2>Veo info komponendid</h2>
                        <table className="order-components-table" style={{width: '50%'}}>
                            <thead>
                            <tr>
                                <th style={{paddingTop: '5px', paddingBottom: '5px'}}>Kuvatud</th>
                                <th style={{paddingTop: '5px', paddingBottom: '5px'}}>Nimi</th>
                                <th style={{paddingTop: '5px', paddingBottom: '5px'}}>Kohustuslik</th>
                            </tr>
                            </thead>
                            <tbody>{orderComponentsState.orderComponents.map((orderComponent) => renderOrderComponent(orderComponent))}</tbody>
                        </table>
                    </div>

                    {requestStatus === RequestStatus.Failed && servicessState.errors.length > 0 && (
                        <div className="message msg-error">
                            <div>
                                <h2>Viga:</h2>
                                {servicessState.errors.map((error, index) => (
                                    <span key={index}>{error}<br/></span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="action clear">
                        <p>
                            <button type="submit">
                                <span><strong>Sisesta</strong></span>
                            </button>
                            <Link to="/services">Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    )

}
